import React, { useEffect, useState } from 'react'
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    IconButton,
    Theme,
    Typography,
    useTheme,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import EmailIcon from '@material-ui/icons/Email'
import PhoneIcon from '@material-ui/icons/Phone'
import LocationOnIcon from '@material-ui/icons/LocationOn'
import BuildIcon from '@material-ui/icons/Build'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline'
import { LoriIcon } from '../../navigation/layouts/Lori/Icons/LoriIcon'
import { formatPhoneForDisplay } from '../../helpers'
import { ListVendor } from '../../models'
import { createServicesString } from './VendorRow'
import { Container } from '../../components'
import { toast } from 'react-toastify'

import loadingAnimation from '../../assets/Loading_Animation.gif'

export type RecommendedVendor = ListVendor & {
    completion_percentage?: number
    completed_work_order_count?: number
}

interface Props {
    open: boolean
    onClose: () => void
    theme: Theme
    fetchRecommendedVendors: () => Promise<{ vendors: ListVendor[] }>
    onAddVendor?: (vendor: ListVendor) => void | Promise<void>
}

export const VendorRecommendModal = (props: Props) => {
    const { open, onClose, theme, fetchRecommendedVendors, onAddVendor } = props
    const muiTheme = useTheme()

    const [loading, setLoading] = useState(false)
    const [vendors, setVendors] = useState<RecommendedVendor[]>([])
    const [error, setError] = useState<string | null>(null)
    const [addingVendorId, setAddingVendorId] = useState<number | null>(null)

    useEffect(() => {
        if (!open) {
            setError(null)
            setVendors([])
            setAddingVendorId(null)
            return
        }
        setLoading(true)
        setError(null)
        fetchRecommendedVendors()
            .then((res) => {
                setVendors((res.vendors ?? []) as RecommendedVendor[])
            })
            .catch((err) => {
                const status = err.response?.status
                const message = err.response?.data?.message
                if (status === 400) {
                    setError(
                        message ||
                            'Property primary address must have latitude and longitude set for distance filtering.',
                    )
                    setVendors([])
                } else if (status === 401) {
                    setError(
                        'You must be signed in to get vendor recommendations.',
                    )
                    setVendors([])
                } else {
                    setError(
                        message ||
                            'Unable to load recommended vendors. Please try again.',
                    )
                    setVendors([])
                    toast.error(message || 'Failed to load recommended vendors')
                }
            })
            .finally(() => {
                setLoading(false)
            })
    }, [open, fetchRecommendedVendors])

    const formatAddress = (v: ListVendor) => {
        const parts = [v.address, v.city, v.state, v.zip_code].filter(Boolean)
        return parts.join(', ') || '—'
    }

    const modalHeight = '85vh'

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                style: {
                    borderRadius: theme.spacing(2.5),
                    height: modalHeight,
                    maxHeight: modalHeight,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            <Box
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: theme.spacing(2.5, 3),
                    flexShrink: 0,
                    background:
                        muiTheme.palette.type === 'light'
                            ? `linear-gradient(to bottom, ${muiTheme.palette.grey[50]}, ${muiTheme.palette.background.paper})`
                            : muiTheme.palette.background.paper,
                    borderBottom: `1px solid ${muiTheme.palette.divider}`,
                }}
            >
                <Box
                    display="flex"
                    alignItems="center"
                    style={{ gap: theme.spacing(1.5) }}
                >
                    <LoriIcon
                        width={28}
                        height={28}
                        color={muiTheme.palette.text.primary}
                    />
                    <Typography
                        variant="h6"
                        style={{
                            fontWeight: 700,
                            color: muiTheme.palette.text.primary,
                            letterSpacing: '-0.02em',
                        }}
                    >
                        Recommended Vendors
                    </Typography>
                </Box>
                <IconButton
                    size="small"
                    onClick={onClose}
                    aria-label="Close"
                    style={{ padding: theme.spacing(1) }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>
            <DialogContent
                style={{
                    padding: theme.spacing(3),
                    paddingTop: theme.spacing(2.5),
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                }}
            >
                {loading && (
                    <Box
                        display="flex"
                        flexDirection="column"
                        flex={1}
                        justifyContent="center"
                        alignItems="center"
                        minHeight={0}
                    >
                        <img
                            src={loadingAnimation}
                            alt="Searching for vendors"
                            style={{
                                display: 'block',
                                marginBottom: theme.spacing(2),
                            }}
                        />
                        <Typography variant="body1" color="textSecondary">
                            Searching for vendors
                        </Typography>
                    </Box>
                )}

                {!loading && error && (
                    <Box
                        display="flex"
                        flexDirection="column"
                        flex={1}
                        alignItems="center"
                        justifyContent="center"
                        padding={4}
                        minHeight={0}
                    >
                        <Typography
                            color="error"
                            align="center"
                            variant="body1"
                            style={{ marginBottom: theme.spacing(2) }}
                        >
                            {error}
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={onClose}
                            style={{ textTransform: 'none' }}
                        >
                            Close
                        </Button>
                    </Box>
                )}

                {!loading && !error && vendors.length === 0 && (
                    <Box
                        display="flex"
                        flexDirection="column"
                        flex={1}
                        alignItems="center"
                        justifyContent="center"
                        padding={4}
                        minHeight={0}
                    >
                        <Typography
                            color="textSecondary"
                            align="center"
                            variant="body1"
                        >
                            No recommended vendors found for this property that
                            have completed 85% of their work.
                        </Typography>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={onClose}
                            style={{
                                marginTop: theme.spacing(2),
                                textTransform: 'none',
                            }}
                        >
                            Close
                        </Button>
                    </Box>
                )}

                {!loading && !error && vendors.length > 0 && (
                    <Box
                        display="flex"
                        flexDirection="column"
                        flex={1}
                        minHeight={0}
                        overflow="hidden"
                    >
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{
                                marginBottom: theme.spacing(2),
                                lineHeight: 1.5,
                                flexShrink: 0,
                            }}
                        >
                            These are Lori-recommended vendors for your
                            property.
                        </Typography>
                        <Box
                            style={{
                                display: 'grid',
                                gridTemplateColumns:
                                    'repeat(auto-fill, minmax(320px, 1fr))',
                                gap: theme.spacing(2),
                                flex: 1,
                                minHeight: 0,
                                overflow: 'auto',
                                paddingRight: theme.spacing(0.5),
                            }}
                        >
                            {vendors.map((vendor) => (
                                <Box
                                    key={vendor.id}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        padding: theme.spacing(2.5),
                                        backgroundColor:
                                            muiTheme.palette.background.paper,
                                        borderRadius: theme.spacing(1.5),
                                        border: `1px solid ${muiTheme.palette.grey[200]}`,
                                        boxShadow: muiTheme.shadows[1],
                                    }}
                                >
                                    <Typography
                                        variant="subtitle1"
                                        style={{
                                            fontWeight: 700,
                                            marginBottom: theme.spacing(1.5),
                                            color:
                                                muiTheme.palette.text.primary,
                                            lineHeight: 1.3,
                                        }}
                                    >
                                        {vendor.name}
                                    </Typography>

                                    {typeof vendor.completed_work_order_count ===
                                        'number' && (
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            style={{
                                                marginBottom: theme.spacing(
                                                    1.5,
                                                ),
                                                gap: theme.spacing(1),
                                                padding: theme.spacing(1, 1.25),
                                                borderRadius: theme.spacing(1),
                                                backgroundColor:
                                                    muiTheme.palette.type ===
                                                    'light'
                                                        ? muiTheme.palette
                                                              .grey[100]
                                                        : muiTheme.palette
                                                              .grey[800],
                                                alignSelf: 'flex-start',
                                            }}
                                        >
                                            <CheckCircleOutlineIcon
                                                style={{
                                                    fontSize: 18,
                                                    color:
                                                        muiTheme.palette.success
                                                            .main,
                                                }}
                                            />
                                            <Typography
                                                variant="body2"
                                                style={{
                                                    fontWeight: 600,
                                                    color:
                                                        muiTheme.palette.text
                                                            .primary,
                                                }}
                                            >
                                                {
                                                    vendor.completed_work_order_count
                                                }{' '}
                                                work order
                                                {vendor.completed_work_order_count ===
                                                1
                                                    ? ''
                                                    : 's'}{' '}
                                                completed
                                            </Typography>
                                        </Box>
                                    )}

                                    {vendor.services?.length > 0 && (
                                        <Box
                                            style={{
                                                marginBottom: theme.spacing(
                                                    1.5,
                                                ),
                                            }}
                                        >
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                style={{
                                                    marginBottom: theme.spacing(
                                                        0.5,
                                                    ),
                                                }}
                                            >
                                                <BuildIcon
                                                    style={{
                                                        fontSize: 14,
                                                        marginRight: theme.spacing(
                                                            0.5,
                                                        ),
                                                        color:
                                                            muiTheme.palette
                                                                .text.secondary,
                                                    }}
                                                />
                                                <Typography
                                                    variant="caption"
                                                    color="textSecondary"
                                                    style={{
                                                        fontWeight: 600,
                                                        textTransform:
                                                            'uppercase',
                                                        letterSpacing: '0.04em',
                                                    }}
                                                >
                                                    Services
                                                </Typography>
                                            </Box>
                                            <Typography
                                                variant="body2"
                                                style={{
                                                    color:
                                                        muiTheme.palette.text
                                                            .secondary,
                                                    lineHeight: 1.5,
                                                    paddingLeft: 20,
                                                }}
                                            >
                                                {createServicesString(vendor)}
                                            </Typography>
                                        </Box>
                                    )}

                                    <Box
                                        style={{
                                            flex: 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: theme.spacing(0.75),
                                        }}
                                    >
                                        {formatAddress(vendor) !== '—' && (
                                            <Box
                                                display="flex"
                                                alignItems="flex-start"
                                                style={{
                                                    gap: theme.spacing(0.75),
                                                }}
                                            >
                                                <LocationOnIcon
                                                    style={{
                                                        fontSize: 14,
                                                        color:
                                                            muiTheme.palette
                                                                .text.secondary,
                                                        marginTop: 2,
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                    style={{
                                                        fontSize: 13,
                                                        lineHeight: 1.4,
                                                    }}
                                                >
                                                    {formatAddress(vendor)}
                                                </Typography>
                                            </Box>
                                        )}
                                        {vendor.phone && (
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                style={{
                                                    gap: theme.spacing(0.75),
                                                }}
                                            >
                                                <PhoneIcon
                                                    style={{
                                                        fontSize: 14,
                                                        color:
                                                            muiTheme.palette
                                                                .text.secondary,
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                    style={{ fontSize: 13 }}
                                                >
                                                    {formatPhoneForDisplay(
                                                        vendor.phone,
                                                    )}
                                                </Typography>
                                            </Box>
                                        )}
                                        {vendor.email && (
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                style={{
                                                    gap: theme.spacing(0.75),
                                                }}
                                            >
                                                <EmailIcon
                                                    style={{
                                                        fontSize: 14,
                                                        color:
                                                            muiTheme.palette
                                                                .text.secondary,
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                    style={{
                                                        fontSize: 13,
                                                        overflow: 'hidden',
                                                        textOverflow:
                                                            'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {vendor.email}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>

                                    {onAddVendor && (
                                        <Button
                                            size="small"
                                            variant="contained"
                                            color="primary"
                                            disabled={
                                                addingVendorId === vendor.id
                                            }
                                            style={{
                                                marginTop: theme.spacing(2),
                                                alignSelf: 'flex-start',
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                paddingLeft: theme.spacing(2),
                                                paddingRight: theme.spacing(2),
                                                minWidth: 140,
                                            }}
                                            onClick={() => {
                                                setAddingVendorId(vendor.id)
                                                Promise.resolve(
                                                    onAddVendor(vendor),
                                                )
                                                    .then(() => {
                                                        setVendors((prev) =>
                                                            prev.filter(
                                                                (v) =>
                                                                    v.id !==
                                                                    vendor.id,
                                                            ),
                                                        )
                                                    })
                                                    .catch(() => {
                                                        toast.error(
                                                            `Could not add ${vendor.name} to property.`,
                                                        )
                                                    })
                                                    .finally(() => {
                                                        setAddingVendorId(null)
                                                    })
                                            }}
                                        >
                                            {addingVendorId === vendor.id ? (
                                                <CircularProgress
                                                    size={20}
                                                    style={{
                                                        color: 'white',
                                                    }}
                                                />
                                            ) : (
                                                'Add to property'
                                            )}
                                        </Button>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    )
}
